/**
 * "Where it has been": the six stages, one row each, from the records that
 * exist (`journeyRows.buildJourney` over the activity and the gate history).
 *
 * A stage the ticket passed prints what happened there last and when; the
 * stage it stands in prints its state sentence and since when; the stages
 * ahead print "Not yet". A stage with no record says what is true of it and
 * claims nothing more.
 */
import type { CSSProperties } from "react";
import type { WorkItem, WorkItemActivityDto, WorkItemGate } from "@yagni/shared";
import { StagePie } from "../ui/stagePie.js";
import { STAGE_HUE } from "../ui/stagePie.js";
import { buildJourney, journeyTime, type JourneyRow } from "../work/journeyRows.js";
import { JOURNEY_FROM_CHAT, TIMELINE_NOTE_READ } from "../work/workCopy.js";
import {
  PIPELINE_STAGE_LABEL,
  PIPELINE_STAGES,
  pipelineStageOf,
  stagePieOf,
  type PipelineStage,
} from "../work/workStage.js";

export interface TimelineRow {
  stage: PipelineStage;
  state: "past" | "now" | "future";
  line: string;
  when: string;
  /**
   * The stage it stands in: the latest note a person added that this stage's
   * session read, with when, under the state sentence (YAG-1116).
   */
  note?: string;
}

/** The six rows, pure, for the block and its test. */
export function timelineRows(
  item: WorkItem,
  journey: readonly JourneyRow[],
  nowSentence: string,
  nowSince: string,
): TimelineRow[] {
  const current = pipelineStageOf(item);
  const finished = item.phase === "done" && current === "done";
  const at = PIPELINE_STAGES.indexOf(current);
  return PIPELINE_STAGES.map((stage, index) => {
    const records = journey.filter((row) => row.stage === stage);
    const last = records[records.length - 1] ?? null;
    if (index < at || finished) {
      const via = last?.fromChat ? ` · ${JOURNEY_FROM_CHAT}` : "";
      const who = last?.actorName ? ` · ${last.actorName}` : "";
      return {
        stage,
        state: "past",
        line: last ? `${last.title}${via}${who}` : "Passed",
        when: last ? journeyTime(last.at) : "",
      };
    }
    if (index === at) {
      // The note is read while the stage runs, so it shows while it does.
      const read = [...records].reverse().find((row) => row.noteRead);
      return {
        stage,
        state: "now",
        line: nowSentence,
        when: nowSince,
        ...(read ? { note: `${TIMELINE_NOTE_READ} · ${journeyTime(read.at)}` } : {}),
      };
    }
    return { stage, state: "future", line: "Not yet", when: "" };
  });
}

export function Timeline({
  item,
  activity,
  gates,
  nowSentence,
  nowSince,
}: {
  item: WorkItem;
  activity: readonly WorkItemActivityDto[];
  gates: readonly WorkItemGate[];
  nowSentence: string;
  nowSince: string;
}) {
  const journey = buildJourney({ item, activity: [...activity], gates: [...gates] });
  const rows = timelineRows(item, journey, nowSentence, nowSince);
  const nowMode = stagePieOf(item).mode;
  return (
    <div className="blk" data-testid="ticket-timeline">
      <div className="bh">
        <span className="label">Where it has been</span>
      </div>
      <div className="timeline">
        {rows.map((row) => (
          <div key={row.stage} className={row.state === "future" ? "future" : undefined}>
            <StagePie
              stage={row.stage}
              mode={row.state === "now" ? nowMode : row.state}
              size={18}
            />
            <span className="s" style={{ "--stage": STAGE_HUE[row.stage] } as CSSProperties}>
              {PIPELINE_STAGE_LABEL[row.stage]}
            </span>
            <span className="w">
              {row.line}
              {row.note ? (
                <span className="note" data-testid="ticket-timeline-note">
                  {row.note}
                </span>
              ) : null}
            </span>
            <span className="d">{row.when}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
